import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/api';

interface User {
  id: number;
  email: string;
  name?: string;
  organization?: string;
  role?: string;
  phone?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, otp: string) => Promise<void>;
  devLogin: (email: string) => Promise<void>;
  requestOTP: (email: string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await api.get('/auth/profile');
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.clear();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const requestOTP = async (email: string) => {
    const response = await api.post('/auth/request-otp', { email });
    return response.data;
  };

  const login = async (email: string, otp: string) => {
    const response = await api.post('/auth/verify-otp', { email, otp });
    const { accessToken, refreshToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setUser(user);
  };

  const devLogin = async (email: string) => {
    const response = await api.post('/auth/dev-login', { email });
    const { accessToken, refreshToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    setUser(user);
  };

  const logout = () => {
    localStorage.clear();
    setUser(null);
    window.location.href = '/login';
  };

  const updateProfile = async (data: Partial<User>) => {
    const response = await api.put('/auth/profile', data);
    setUser(response.data.user);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, devLogin, requestOTP, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};